import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerCreateOrUpdateRequest, CustomerDetail, CustomerReservationSummary } from '@models/customers';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CodeListsService } from '@services/codelists.service';
import { CustomersService } from '@services/customers.service';
import { ToastService } from '@services/toast.service';

interface EmailAddressForm {
  id: FormControl<number | null>;
  email: FormControl<string | null>;
  label: FormControl<string | null>;
  isPrimary: FormControl<boolean>;
}

interface PhoneNumberForm {
  id: FormControl<number | null>;
  number: FormControl<string | null>;
  phoneNumberTypeId: FormControl<number | null>;
  isPrimary: FormControl<boolean>;
}

interface CustomerForm {
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  genderId: FormControl<number | null>;
  statusId: FormControl<number | null>;
  siteId: FormControl<number | null>;
  newsletter: FormControl<boolean>;
  isActive: FormControl<boolean>;
  isDummyCustomer: FormControl<boolean>;
  address: FormControl<string | null>;
  postalCode: FormControl<string | null>;
  city: FormControl<string | null>;
  countryId: FormControl<number | null>;
  emailAddresses: FormArray<FormGroup<EmailAddressForm>>;
  phoneNumbers: FormArray<FormGroup<PhoneNumberForm>>;
}

@Component({
  selector: 'app-customer-form',
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    TranslatePipe,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
  ],
  templateUrl: './customer-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerFormComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly customersService = inject(CustomersService);
  readonly codeListsService = inject(CodeListsService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly customerId = signal<number | null>(null);
  readonly pageTitle = signal('');
  readonly recentReservations = signal<CustomerReservationSummary[]>([]);
  private backUrl = '/customers';

  readonly form = new FormGroup<CustomerForm>({
    firstName: new FormControl<string | null>('', Validators.required),
    lastName: new FormControl<string | null>('', Validators.required),
    genderId: new FormControl<number | null>(null, Validators.required),
    statusId: new FormControl<number | null>(null, Validators.required),
    siteId: new FormControl<number | null>(null, Validators.required),
    newsletter: new FormControl(false, { nonNullable: true, validators: Validators.required }),
    isActive: new FormControl(true, { nonNullable: true, validators: Validators.required }),
    isDummyCustomer: new FormControl(false, { nonNullable: true, validators: Validators.required }),
    address: new FormControl<string | null>(''),
    postalCode: new FormControl<string | null>(''),
    city: new FormControl<string | null>(''),
    countryId: new FormControl<number | null>(null),
    emailAddresses: new FormArray<FormGroup<EmailAddressForm>>([]),
    phoneNumbers: new FormArray<FormGroup<PhoneNumberForm>>([]),
  });

  get emailAddresses(): FormArray<FormGroup<EmailAddressForm>> {
    return this.form.controls.emailAddresses;
  }

  get phoneNumbers(): FormArray<FormGroup<PhoneNumberForm>> {
    return this.form.controls.phoneNumbers;
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = idParam ? +idParam : null;
    this.customerId.set(id);
    this.backUrl = this.route.snapshot.queryParamMap.get('backUrl') ?? '/customers';

    this.loading.set(true);

    if (id) {
      this.customersService.getCustomer(id).subscribe({
        next: (customer) => {
          this.populateForm(customer);
          this.pageTitle.set([customer.firstName, customer.lastName].filter(Boolean).join(' '));
          this.loading.set(false);
        },
        error: (err) => {
          this.loading.set(false);
          this.toast.showError(err);
          this.navigateToList();
        },
      });

      this.customersService.getCustomerReservations(id).subscribe({
        next: (reservations) => this.recentReservations.set(reservations),
        error: () => this.toast.showError(this.translate.instant('customers.reservations.load-error')),
      });
    } else {
      this.pageTitle.set('');
      this.addEmailAddress();
      this.loading.set(false);
    }
  }

  private populateForm(customer: CustomerDetail): void {
    this.form.patchValue({
      firstName: customer.firstName,
      lastName: customer.lastName,
      genderId: customer.genderId,
      statusId: customer.statusId,
      siteId: customer.siteId,
      newsletter: customer.newsletter,
      isActive: customer.isActive,
      isDummyCustomer: customer.isDummyCustomer,
      address: customer.address,
      postalCode: customer.postalCode,
      city: customer.city,
      countryId: customer.countryId,
    });

    this.emailAddresses.clear();
    for (const e of customer.emailAddresses) {
      this.emailAddresses.push(this.createEmailGroup(e.id, e.email, e.label, e.isPrimary));
    }
    if (this.emailAddresses.length === 0) this.addEmailAddress();

    this.phoneNumbers.clear();
    for (const p of customer.phoneNumbers) {
      this.phoneNumbers.push(this.createPhoneGroup(p.id, p.number, p.phoneNumberTypeId, p.isPrimary));
    }
  }

  addEmailAddress(): void {
    const isPrimary = this.emailAddresses.length === 0;
    this.emailAddresses.push(this.createEmailGroup(null, '', '', isPrimary));
  }

  removeEmailAddress(index: number): void {
    const wasPrimary = this.emailAddresses.at(index).controls.isPrimary.value;
    this.emailAddresses.removeAt(index);
    if (wasPrimary && this.emailAddresses.length > 0) {
      const newPrimary = this.emailAddresses.at(0);
      newPrimary.controls.isPrimary.setValue(true);
      newPrimary.controls.email.setValidators([Validators.required, Validators.email]);
      newPrimary.controls.email.updateValueAndValidity();
    }
  }

  setPrimaryEmail(index: number): void {
    this.emailAddresses.controls.forEach((group, i) => {
      group.controls.isPrimary.setValue(i === index);
      group.controls.email.setValidators(i === index ? [Validators.required, Validators.email] : [Validators.email]);
      group.controls.email.updateValueAndValidity();
    });
  }

  addPhoneNumber(): void {
    this.phoneNumbers.push(this.createPhoneGroup(null, null, null, this.phoneNumbers.length === 0));
  }

  removePhoneNumber(index: number): void {
    const wasPrimary = this.phoneNumbers.at(index).controls.isPrimary.value;
    this.phoneNumbers.removeAt(index);
    if (wasPrimary && this.phoneNumbers.length > 0) {
      this.phoneNumbers.at(0).controls.isPrimary.setValue(true);
    }
  }

  setPrimaryPhone(index: number): void {
    this.phoneNumbers.controls.forEach((group, i) => {
      group.controls.isPrimary.setValue(i === index);
    });
  }

  private navigateToList(): void {
    this.router.navigateByUrl(this.backUrl);
  }

  cancel(): void {
    this.navigateToList();
  }

  private createEmailGroup(
    id: number | null,
    email: string | null,
    label: string | null,
    isPrimary: boolean,
  ): FormGroup<EmailAddressForm> {
    return new FormGroup<EmailAddressForm>({
      id: new FormControl<number | null>(id),
      email: new FormControl<string | null>(
        email,
        isPrimary ? [Validators.required, Validators.email] : [Validators.email],
      ),
      label: new FormControl<string | null>(label),
      isPrimary: new FormControl(isPrimary, { nonNullable: true }),
    });
  }

  private createPhoneGroup(
    id: number | null,
    number: string | null,
    phoneNumberTypeId: number | null,
    isPrimary: boolean,
  ): FormGroup<PhoneNumberForm> {
    return new FormGroup<PhoneNumberForm>({
      id: new FormControl<number | null>(id),
      number: new FormControl<string | null>(number),
      phoneNumberTypeId: new FormControl<number | null>(phoneNumberTypeId),
      isPrimary: new FormControl(isPrimary, { nonNullable: true }),
    });
  }

  getReservationStatusClass(label: string | null): string {
    const map: Record<string, string> = {
      important: 'bg-red-100 text-red-800',
      warning: 'bg-amber-100 text-amber-800',
      info: 'bg-blue-100 text-blue-800',
      inverse: 'bg-gray-200 text-gray-800',
      success: 'bg-green-100 text-green-800',
    };
    return `inline-block px-2 py-0.5 rounded text-sm font-medium ${map[label ?? ''] ?? 'bg-gray-100 text-gray-700'}`;
  }

  save(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const value = this.form.getRawValue();
    if (value.statusId == null) return;

    const payload: CustomerCreateOrUpdateRequest = {
      firstName: value.firstName,
      lastName: value.lastName,
      genderId: value.genderId,
      statusId: value.statusId,
      siteId: value.siteId,
      newsletter: value.newsletter,
      isActive: value.isActive,
      isDummyCustomer: value.isDummyCustomer,
      address: value.address,
      postalCode: value.postalCode,
      city: value.city,
      countryId: value.countryId,
      emailAddresses: value.emailAddresses.map((e) => ({
        id: e.id,
        email: e.email,
        label: e.label,
        isPrimary: e.isPrimary,
      })),
      phoneNumbers: value.phoneNumbers.map((p) => ({
        id: p.id,
        number: p.number,
        phoneNumberTypeId: p.phoneNumberTypeId,
        isPrimary: p.isPrimary,
      })),
    };

    this.saving.set(true);
    const id = this.customerId();

    const request$ = id
      ? this.customersService.updateCustomer(id, payload)
      : this.customersService.createCustomer(payload);

    request$.subscribe({
      next: () => {
        const key = id ? 'customers.form.save-success-edit' : 'customers.form.save-success-create';
        this.toast.showSuccess(this.translate.instant(key));
        this.navigateToList();
      },
      error: (err) => {
        this.saving.set(false);
        this.toast.showError(err);
      },
    });
  }
}
